/**
 * a11y.ts — WCAG 2.1 AA accessibility helpers for popup mode.
 *
 * Focus trap:   Tab/Shift+Tab cycles through focusable elements inside the modal.
 * Escape close: Pressing Escape fires the provided onClose callback.
 * Scroll lock:  Adds/removes body.mp-scroll-locked to prevent background scroll.
 * Return focus: Stores the trigger element and restores focus on close.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'iframe',
].join(', ')

interface TrapOptions {
  container: HTMLElement
  onEscape: () => void
}

interface FocusTrap {
  activate: () => void
  deactivate: () => void
}

export function createFocusTrap({ container, onEscape }: TrapOptions): FocusTrap {
  let prevFocus: HTMLElement | null = null

  function getFocusable(): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      el => !el.closest('[hidden]') && el.offsetParent !== null,
    )
  }

  function handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault()
      onEscape()
      return
    }

    if (e.key !== 'Tab') return

    const focusable = getFocusable()
    if (focusable.length === 0) {
      e.preventDefault()
      return
    }

    const first = focusable[0]
    const last  = focusable[focusable.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  return {
    activate() {
      prevFocus = document.activeElement as HTMLElement | null
      document.addEventListener('keydown', handleKeyDown)
      document.body.classList.add('mp-scroll-locked')

      // Focus the first focusable element (the iframe)
      const first = getFocusable()[0]
      if (first) {
        // Small delay so the transition has started before we steal focus
        setTimeout(() => first.focus(), 50)
      }
    },

    deactivate() {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.classList.remove('mp-scroll-locked')
      prevFocus?.focus()
    },
  }
}

export function lockScroll(): void {
  document.body.classList.add('mp-scroll-locked')
}

export function unlockScroll(): void {
  document.body.classList.remove('mp-scroll-locked')
}
